import { useCallback, useMemo } from 'react';

import { isDefined } from 'common';

import { useStableValue } from '../../../../../hooks/useStableValue';
import { type ReactFlowBlockNode, type ReactFlowEdge } from '../../types';

export function getNodes(
  nodesById: Map<string, ReactFlowBlockNode>,
  nodeIds: string[]
): ReactFlowBlockNode[] {
  return nodeIds.map((parentId) => nodesById.get(parentId)).filter(isDefined);
}

function getNodeNames(nodesById: Map<string, ReactFlowBlockNode>, nodeIds: string[]): string[] {
  return getNodes(nodesById, nodeIds).map((node) => node.data.label);
}

interface UseFlowViewRelationshipsProps {
  blockNodes: ReactFlowBlockNode[];
  edges: ReactFlowEdge[];
}

export function useFlowViewRelationships(props: UseFlowViewRelationshipsProps) {
  const { blockNodes, edges } = props;

  const nodesById = useMemo(() => {
    return new Map<string, ReactFlowBlockNode>(blockNodes.map((node) => [node.id, node]));
  }, [blockNodes]);

  const namesById = useStableValue(
    useMemo((): Record<string, string> => {
      const names: Record<string, string> = {};
      for (const node of blockNodes) {
        names[node.id] = node.data.label;
      }
      return names;
    }, [blockNodes])
  );

  const blockIdsByName = useStableValue(
    useMemo(() => new Map(blockNodes.map((block) => [block.data.label, block.id])), [blockNodes])
  );

  const parentNodeIdsById = useStableValue(
    useMemo(() => {
      const nodeIds = new Map<string, Set<string>>();
      for (const edge of edges) {
        if (!nodeIds.has(edge.target)) {
          nodeIds.set(edge.target, new Set());
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        nodeIds.get(edge.target)!.add(edge.source);
      }
      return nodeIds;
    }, [edges])
  );

  const getAllParentNodeIds = useCallback(
    (nodeId: string) => {
      const visitedNodeIds = new Set<string>();
      const allParentNodeIds: string[] = [];

      /**
       * DFS to fill all parent block ids, ignoring cycles
       */
      const fillAllParentNodeIds = (currentNodeId: string) => {
        if (visitedNodeIds.has(currentNodeId)) {
          return;
        }
        visitedNodeIds.add(currentNodeId);

        const parentIds = parentNodeIdsById.get(currentNodeId);
        if (parentIds) {
          for (const parentId of parentIds) {
            if (!visitedNodeIds.has(parentId)) {
              allParentNodeIds.push(parentId);
              fillAllParentNodeIds(parentId);
            }
          }
        }
      };

      fillAllParentNodeIds(nodeId);
      return allParentNodeIds;
    },
    [parentNodeIdsById]
  );

  const getAllParentNodeNames = useCallback(
    (nodeId: string) => {
      return getNodeNames(nodesById, getAllParentNodeIds(nodeId));
    },
    [getAllParentNodeIds, nodesById]
  );
  return {
    nodesById,
    namesById,
    blockIdsByName,
    getAllParentNodeIds,
    getAllParentNodeNames,
  };
}
